import React, { useState } from 'react'
import {
  Group,
  TextInput,
  Select,
  Switch,
  Button,
  Drawer,
  Stack,
  Box
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { DatePickerInput } from '@mantine/dates'

export interface FilterDef {
  key: string;
  label: string;
  type: 'text' | 'select' | 'toggle' | 'daterange';
  options?: { label: string; value: string }[];
}

interface Props {
  filters: FilterDef[];
  onFilterChange: (key: string, value: unknown) => void;
}

export default function FilterBar ({ filters, onFilterChange }: Props) {
  const mobile = useMediaQuery('(max-width: 768px)')
  const [drawerOpened, setDrawerOpened] = useState(false)

  const handleTextChange = (key: string, value: string) => {
    onFilterChange(key, value)
  }

  const handleSelectChange = (key: string, value: string | null) => {
    onFilterChange(key, value ?? undefined)
  }

  const handleToggleChange = (key: string, value: boolean) => {
    onFilterChange(key, value)
  }

  const handleDaterangeChange = (
    key: string,
    value: [Date | null, Date | null]
  ) => {
    onFilterChange(key, value)
  }

  const renderFilter = (f: FilterDef) => {
    switch (f.type) {
      case 'text':
        return (
          <TextInput
            key={f.key}
            label={f.label}
            placeholder={f.label}
            size="sm"
            onChange={(e) => handleTextChange(f.key, e.currentTarget.value)}
          />
        )
      case 'select':
        return (
          <Select
            key={f.key}
            label={f.label}
            placeholder={f.label}
            size="sm"
            data={f.options ?? []}
            clearable
            onChange={(v) => handleSelectChange(f.key, v)}
          />
        )
      case 'toggle':
        return (
          <Switch
            key={f.key}
            label={f.label}
            size="sm"
            onChange={(e) => handleToggleChange(f.key, e.currentTarget.checked)}
          />
        )
      case 'daterange':
        return (
          <DatePickerInput
            key={f.key}
            type="range"
            label={f.label}
            placeholder="Pick range"
            size="sm"
            onChange={(v) => handleDaterangeChange(f.key, v as [Date | null, Date | null])}
          />
        )
      default:
        return null
    }
  }

  const content = (
    <Group spacing="md" align="flex-end">
      {filters.map(renderFilter)}
    </Group>
  )

  const stackedContent = (
    <Stack spacing="md">
      {filters.map(renderFilter)}
    </Stack>
  )

  if (mobile) {
    return (
      <Box>
        <Button
          variant="light"
          size="sm"
          onClick={() => setDrawerOpened(true)}
        >
          Filters
        </Button>
        <Drawer
          opened={drawerOpened}
          onClose={() => setDrawerOpened(false)}
          title="Filters"
          position="right"
          padding="md"
        >
          {stackedContent}
        </Drawer>
      </Box>
    )
  }

  return content
}
